import React, { Component } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import PDFViewerContainer from "../PDFViewerContainer";
import SplitView from "../../__shared__/IconComponents/SplitView";
import { ATTACHMENTS_ERROR_MESSAGES } from "../constants";
import { AVENDA_EMAIL_REGEX_PATTERN } from "../../__shared__/Sidebar/constants";
import "./styles.css";

const TAB_TITLES = {
    pathology: "Pathology Report",
    summary: "Pathology Summary",
    template: "Template",
};

class PDFViewerWrapper extends Component {
    state = {
        openTab: "pathology",
        haveTabsSplit: false,
        containerWidth: 600,
        splitViewOpenTab: "summary",
    };

    pdfContainerRef = React.createRef();

    componentDidMount() {
        // Calculate width once the component mounts
        if (this.pdfContainerRef.current) {
            const containerWidth = this.pdfContainerRef.current.offsetWidth;
            if (containerWidth !== this.state.containerWidth) {
                this.setState({ containerWidth });
            }
        }
        window.addEventListener("resize", this.updateContainerWidth);
    }

    componentWillUnmount() {
        window.removeEventListener("resize", this.updateContainerWidth);
    }

    updateContainerWidth = () => {
        if (this.pdfContainerRef.current) {
            const containerWidth = this.pdfContainerRef.current.offsetWidth;
            if (containerWidth !== this.state.containerWidth) {
                this.setState({ containerWidth });
            }
        }
    };

    hasAccessToSummaryTab = () => {
        return this.props.userData?.email?.match(AVENDA_EMAIL_REGEX_PATTERN);
    };

    handleTabClick = (tabName) => {
        this.setState({ openTab: tabName });
    };

    handleSplitViewTabClick = (tab) => {
        this.setState({
            splitViewOpenTab: tab,
        });
    };

    toggleSplitView = () => {
        this.setState((prevState) => ({
            haveTabsSplit: !prevState.haveTabsSplit,
        }));
    };

    renderTab(tab) {
        return (
            <div
                id={`pdf-tab-${tab}`}
                className={`pathTab ${
                    this.state.openTab === tab || this.state.haveTabsSplit
                        ? "pdf-tab-active"
                        : ""
                }`}
                onClick={() => this.handleTabClick(tab)}
            >
                {TAB_TITLES[tab]}
            </div>
        );
    }

    renderSplitViewTab(tab) {
        return (
            <div
                className={`pathTab ${this.state.splitViewOpenTab === tab ? "pdf-tab-active" : ""}`}
                onClick={() => this.handleSplitViewTabClick(tab)}
            >
                {TAB_TITLES[tab]}
            </div>
        );
    }

    renderHeader() {
        const { haveTabsSplit } = this.state;
        const hasAccessToSummaryTab = this.hasAccessToSummaryTab();

        return (
            <div className="header">
                <div className="tabs">
                    {this.renderTab("pathology")}
                    {!haveTabsSplit &&
                        hasAccessToSummaryTab &&
                        this.renderTab("summary")}
                    {!haveTabsSplit && this.renderTab("template")}
                </div>
                <div className="split-button" onClick={this.toggleSplitView}>
                    <SplitView />
                </div>
            </div>
        );
    }

    renderSplitViewHeader() {
        const hasAccessToSummaryTab = this.hasAccessToSummaryTab();

        return (
            <div className="header">
                <div className="tabs">
                    {hasAccessToSummaryTab &&
                        this.renderSplitViewTab("summary")}
                    {this.renderSplitViewTab("template")}
                </div>
            </div>
        );
    }

    getPanelClassName() {
        const { haveTabsSplit, openTab } = this.state;

        if (haveTabsSplit) {
            return { primary: "path", secondary: "template" };
        }

        const isPathReportTabActive = openTab === "pathology";

        return {
            primary: isPathReportTabActive ? "expand" : "expand none",
            secondary: isPathReportTabActive ? "expand none" : "expand",
        };
    }

    renderSecondaryContent() {
        const { haveTabsSplit, openTab, splitViewOpenTab } = this.state;
        const hasAccessToSummary = this.hasAccessToSummaryTab();

        if (haveTabsSplit) {
            const tab = hasAccessToSummary ? splitViewOpenTab : "template";
            return this.renderContentViewer(tab);
        }

        if (openTab === "summary" || openTab === "template") {
            return this.renderContentViewer(openTab);
        }

        return null;
    }

    getPathologySummaryError() {
        const { SavedData, TreatmentPlan } = this.props.currentTreatmentPlan;

        const isPathReportUploaded =
            SavedData?.IsPathReportUploaded &&
            this.props.pathReportURL?.length > 1;

        if (!isPathReportUploaded) {
            return ATTACHMENTS_ERROR_MESSAGES.NO_PATH_SUMMARY_AVAILABLE;
        }

        const isPathSummaryUploaded =
            SavedData?.IsPathologySummaryUploaded &&
            this.props.pathSummaryURL?.length > 1;

        const pathSummaryGenerationStatus =
            TreatmentPlan?.PatientData?.PathologySummaryData?.Status;

        const isPathSummaryGenerationSuccessful =
            isPathReportUploaded &&
            isPathSummaryUploaded &&
            pathSummaryGenerationStatus === "SUCCESS";

        if (isPathSummaryGenerationSuccessful) {
            return null;
        }

        if (pathSummaryGenerationStatus === "FAILED") {
            return ATTACHMENTS_ERROR_MESSAGES.PATH_SUMMARY_GENERATION_FAILED;
        }

        return ATTACHMENTS_ERROR_MESSAGES.PATH_SUMMARY_GENERATION_IN_PROGRESS;
    }

    renderContentViewer(viewerType) {
        const isPathologySummaryUploaded =
            this.props.currentTreatmentPlan?.SavedData
                ?.IsPathologySummaryUploaded;
        const patientData =
            this.props.currentTreatmentPlan?.TreatmentPlan?.PatientData;

        const viewerConfig = {
            pathology: {
                url: patientData?.PathologyReportURI,
                data: this.props.pathReportURL,
                error: ATTACHMENTS_ERROR_MESSAGES.NO_PATHOLOGY_REPORT_UPLOADED,
            },
            summary: {
                url:
                    isPathologySummaryUploaded &&
                    this.props.pathReportURL?.length > 1
                        ? patientData?.PathologySummaryData?.PathologySummaryURI
                        : null,
                data: this.props.pathSummaryURL,
                error: this.getPathologySummaryError(),
            },
            template: {
                url: patientData?.TemplateURI,
                data: this.props.templateURL,
                error: ATTACHMENTS_ERROR_MESSAGES.NO_TEMPLATE_UPLOADED,
            },
        };

        const config = viewerConfig[viewerType];
        if (!config) {
            return null;
        }

        const hasURL = config.url?.length > 1;
        const hasData = config.data?.length > 1;

        if (!hasURL || !hasData) {
            return (
                <div id="pdf-err-msg" className="px-4 text-center">
                    {config.error}
                </div>
            );
        }

        return (
            <PDFViewerContainer
                key={config.url + this.state.containerWidth}
                selectedPDF={config.data}
                setPdfLoaded={this.props.setPdfLoaded}
                containerWidth={this.state.containerWidth}
            />
        );
    }

    render() {
        const { haveTabsSplit, openTab } = this.state;
        const {
            primary: primaryPanelClassName,
            secondary: secondaryPanelClassName,
        } = this.getPanelClassName();

        return (
            <div ref={this.pdfContainerRef} className="not-split">
                {/* Top header: 3 tabs (non-split) or pathology-only (split) */}
                {this.renderHeader()}

                {/* Primary panel: Pathology Report */}
                <div className={primaryPanelClassName}>
                    {(haveTabsSplit || openTab === "pathology") &&
                        this.renderContentViewer("pathology")}
                </div>

                {/* Split view header: Summary / Template tabs */}
                {haveTabsSplit && this.renderSplitViewHeader()}

                {/* Secondary panel: Summary or Template */}
                <div className={secondaryPanelClassName}>
                    {this.renderSecondaryContent()}
                </div>
            </div>
        );
    }
}

PDFViewerWrapper.propTypes = {
    pathReportURL: PropTypes.string,
    pathSummaryURL: PropTypes.string,
    templateURL: PropTypes.string,
    setPdfLoaded: PropTypes.func,
    currentTreatmentPlan: PropTypes.object,
    userData: PropTypes.object,
};

const mapStateToProps = (state) => ({
    pathReportURL: state.CreatePlanBiopsyReducer.pathReportURL,
    pathSummaryURL: state.CreatePlanBiopsyReducer.pathSummaryURL,
    templateURL: state.CreatePlanBiopsyReducer.templateURL,
    userData: state.LoginReducer.userData,
});

export default connect(mapStateToProps)(PDFViewerWrapper);
